import firebaseService from 'firebase-service'
import App from './App.vue'
import Domain from 'domain-service'
import Env from 'env-service'
import GuiBus from 'gui-bus-plugin'
import VueGtag from 'vue-gtag'
import billingAccountInterface from 'bs-interface-billing-account'
import AccountInterface from 'bs-interface-account'
import advertiserFactory from 'bs-interface-advertiser'
import CampaignInterfaceFactory from 'bs-interface-campaign'
// use IIFE to allow async/await feature for the whole login process
;(async () => {
  function loadScript(scrpt) {
    return new Promise(resolve => {
      scrpt.addEventListener('load', resolve)
      document.head.appendChild(scrpt)
    })
  }

  let scriptPromises = []
  // Load externalized BSCS JS files - make sure they load only once and leverage browser cache
  if (typeof window.adacadoBSCS != 'object') {
    window.adacadoBSCS = {}
    const scrpt = document.createElement('script')
    scrpt.setAttribute('src', '/assets/v1/adacadoBSCS.min.js') // This url must not contain version number, to allow for hot-swapping
    // ensure that BSCS is loaded before going next step
    scriptPromises.push(loadScript(scrpt))
  }

  // Load externalized Vuetify JS files - make sure they load only once and leverage browser cache
  if (typeof window.Vuetify != 'function') {
    window.Vuetify = () => {}
    const scrpt = document.createElement('script')
    scrpt.setAttribute('src', '/assets/v1/vuetify.min.js') // This url must not contain version number, to allow for hot-swapping
    // ensure that Vuetify is loaded before going next step
    scriptPromises.push(loadScript(scrpt))
  }
  if (typeof VuetifyAdacadoTheme != 'object') {
    const scrpt = document.createElement('script')
    scrpt.setAttribute('src', '/assets/v1/vuetifyAdacadoTheme.js') // This url must not contain version number, to allow for hot-swapping
    // ensure that VuetifyAdacadoTheme is loaded before going next step
    scriptPromises.push(loadScript(scrpt))
  }
  await Promise.all(scriptPromises)

  // Adacado's own advertiser pixel script
  const productIds = [1011, 1012]
  const randomProductId = productIds[Math.floor(Math.random() * productIds.length)]
  const adacadoPixelScript = document.createElement('script')
  adacadoPixelScript.setAttribute('src', 'https://content.adacado.com/productpixel/v1/shell.umd.js')
  adacadoPixelScript.setAttribute('data-advertiserid', '21755')
  adacadoPixelScript.setAttribute('data-productid', randomProductId)
  adacadoPixelScript.setAttribute('id', 'adavadael')
  adacadoPixelScript.setAttribute('async', 'async')
  document.body.appendChild(adacadoPixelScript)

  const cssAssets = [
    '/assets/v1/materialdesignicons.min.css',
    '/assets/v1/roboto-fontface.css',
    '/assets/v1/vuetify.min.css',
    '/assets/v1/adacado.css',
  ]
  const stylesheets = Array.from(document.styleSheets)
  cssAssets
    .map(ca => {
      if (Env.IS_LOCAL_CONNECTION) {
        return `https://localhost:8080${ca}`
      } else {
        return Domain.decorateWithApplicationDomain`${ca}`
      }
    })
    .forEach(ca => {
      if (!stylesheets.find(s => s.href == ca)) {
        const newStyleTag = document.createElement('link')
        newStyleTag.rel = 'stylesheet'
        newStyleTag.href = ca
        document.head.appendChild(newStyleTag)
      }
    })

  // Use Adacado Pub/Sub for inter-FEMS communication
  Vue.use(GuiBus)

  Vue.config.productionTip = false

  Vue.use(Vuetify)

  Vue.use(VueGtag, {
    config: {
      id: 'G-TR916QP441',
      params: {
        send_page_view: true,
      },
    },
  })

  // Initialize global Vue Filters, before main Vue app
  Vue.filter('capitalize', function(value) {
    if (!value) return ''
    value = value.toString()
    return value.charAt(0).toUpperCase() + value.slice(1)
  })

  function processUserLogin() {
    // to prevent users with disabled billing account from logging in,
    // first check if the billing account is disabled
    // if they are not disabled, then determine to redirect them to campaign-creative or campaign-list
    // else, the application will log them out and display an error
    // only do the above if this page isn't loaded from
    billingAccountInterface(firebaseUser)
      .getIsDisabled(async isDisabled => {
        if (isDisabled) {
          firebaseService.auth().signOut()
          localStorage.setItem(
            'adaSystemMsg',
            '{"isShownImmediately":true,"type":"error","message":"Your billing account has been disabled. Please contact support if you wish to resolve this issue."}'
          )
          location.reload()
          return
        }
        // no redirect scenarios:
        // 1. the window is less than 1000px wide, don't redirect, App.vue will handle this
        // 2. the url contains "/mo/quickstart", allow for quick-start usage of this FEMS, ie. don't redirect to the campaign manager page
        if (window.innerWidth < 1000 || window.location.pathname.includes('/mo/quickstart')) {
          init()
          return
        }
        let advertisersList = undefined
        // redirect user based on number of advertiser/campaigns
        // If someone has zero or more than one advertiser, drop them on the advertiser list page.
        // If someone has one advertiser and zero or more than one campaign, drop them off on the campaign manager page.
        // If someone has one advertiser and one campaign, drop them off on the creative editor page.
        await new Promise(resolve => {
          advertiserFactory().getAll(advertisers => {
            if (advertisers.length === 0 || advertisers.length > 1) {
              window.location = '/campaign-manager/v1/page'
            } else {
              advertisersList = advertisers
              resolve()
            }
          })
        })
        await CampaignInterfaceFactory().getList(advertisersList[0].AdvertiserID, campaigns => {
          if (campaigns.length === 0 || campaigns.length > 1) {
            window.location = '/campaign-manager/v1/page'
          } else {
            window.location = `/campaign-creative/v2/page/ai/${advertisersList[0].AdvertiserID}/ci/${campaigns[0].CampaignID}`
          }
        })
      })
      .catch(() => {
        // TODO - if the accountbillingaccount service is down for whatever reason at this moment, a user
        // with a disabled billing account will be able to log in, the BSCS needs to be used here to determine
        // the exact error code/message to handle the difference between the service being down, or an account not
        // being found (ie. during sign up)
        window.location = '/campaign-manager/v1/page'
      })
  }

  async function registerFlowCallback(response) {
    switch (response) {
      case 'ACCOUNT_DOES_NOT_EXIST':
        await firebaseUser.delete().catch(err => {
          firebaseUser = null
          console.error(err)
        })
        init()
        break
      case 'ACCOUNT_EXISTS':
        // ACCOUNT_EXISTS = step 0 only happens on signup error (like BE service being down), should not occur otherwise, user must contact us to proceed
        await firebaseUser.delete().catch(err => {
          firebaseUser = null
          console.error(err)
        })
        localStorage.setItem(
          'adaSystemMsg',
          JSON.stringify({
            isShownImmediately: true,
            type: 'error',
            message:
              'Your account has issues and we are unable to log you in. Please contact support to continue.',
            displayTime: -1,
          })
        )
        init()
        break
      case 'ACCOUNT_REGISTERED':
        // User already has a full account, process login as normal
        processUserLogin()
        break
    }
  }

  // CHECK IF USER WAS ALREADY LOGGED IN AND IF SO REDIRECT TO CORRECT PLACE
  // Function is not used as general login listener else login with gmail with non-registered users is impossible to handle with Vue (Can not disable loaded icons (at minimum))
  let firebaseUser = null
  let isFirebaseUserChecked = false
  firebaseService.auth().onAuthStateChanged(async user => {
    if (isInitComplete) return
    firebaseUser = user
    if (firebaseUser !== null) {
      // Page should not be initialized to check for any redirect requirements (user already logged in)
      // Initialize BSCS singleton with Firebase User
      adacadoBSCS.install({ firebaseUser })
      isFirebaseUserChecked = true
      // We have a user, let's check the user state by using the registered function:
      AccountInterface().registered(user.email, registerFlowCallback)
    } else {
      isFirebaseUserChecked = true
      init()
    }
  })

  // Render login screen (redirect might happen after screen is rendered (too bad))
  // Attach render function to window
  window.adacado_render_functions = window.adacado_render_functions || {}
  let containerId = undefined
  // femsName and femsVersion are injected by webpack - see vue.config.js
  // eslint-disable-next-line
  window.adacado_render_functions[`${__femsName__}_${__femsVersion__}`] = c => {
    containerId = c
    init()
  }

  let isInitComplete = false
  function init() {
    // Proceed to instantiate application only if:
    // - Firebase onAuthStateChanged has fired
    // - FEMS application render function has fired
    if (isFirebaseUserChecked && containerId !== undefined && !isInitComplete) {
      isInitComplete = true
      // Data needed to initialize this FEMS should come from (in order of priority)
      // 1) Restful URL
      // 2) Parent DOM element data- attributes
      const parentContainer = document.getElementById(containerId)
      const childContainer = parentContainer && parentContainer.querySelector('div')
      Vue.prototype.$firebaseUser = firebaseUser
      new Vue({
        vuetify: new Vuetify({
          theme: {
            themes: VuetifyAdacadoTheme,
          },
        }),
        el: childContainer || parentContainer,
        render(h) {
          return h(App, { props: { dataset: parentContainer.dataset } })
        },
      })
    }
  }
})()
